import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import api from '../../../api/axiosInstance';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { toStartOfDayISO, toEndOfDayISO } from '../utils/dashboardDateUtils';

export type MostFailedStages = { stage: string; total: number };

export async function getMostFailedStages(startDate: string, endDate: string): Promise<MostFailedStages[]> {
  const baseUrl = '/api/dashboard-metric/most-failed-stages';
  const startISO = toStartOfDayISO(startDate);
  const endISO = toEndOfDayISO(endDate);
  const url = `${baseUrl}?startDate=${encodeURIComponent(startISO)}&endDate=${encodeURIComponent(endISO)}`;
  const { data } = await api.get(url);
  return (data ?? []).slice(0, 4); // Top 4 failed stages
}

function isToday(dateStr: string): boolean {
  return dateStr === new Date().toISOString().slice(0, 10);
}

export function useMostFailedStages(startDate: string, endDate: string) {
  const { addNotifications } = useSwobNotifications();
  const [hasError, setHasError] = useState(false);
  const endDateIsToday = isToday(endDate);

  const query = useQuery<MostFailedStages[]>({
    queryKey: ['mostFailedStages', startDate, endDate],
    queryFn: () => getMostFailedStages(startDate, endDate),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(startDate && endDate),
    // refetches when the user selects today's date every 1 minute
    refetchInterval: !hasError && endDateIsToday ? 60000 : false
  });

  useEffect(() => {
    if (query.error && !hasError) {
      setHasError(true);
      addNotifications([
        {
          title: 'Error - Dashboard Metrics',
          icon: 'icon-triangle-warning',
          description: 'An error occurred while fetching the most frequently failed stages',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  }, [query.error, hasError, addNotifications]);

  return query;
}
